// Import attendance from the OnTime monthly report (.xls) — upload → preview → commit.
//
// The bridge fix waits on the OnTime vendor; this does not. HR exports the Crystal Reports monthly
// file and it carries the punches the bridge replaced with zeros.
//
// PREVIEW IS NOT OPTIONAL. The upload step parses and diffs but writes nothing; the commit step
// re-parses the same stored file. So the screen below always shows what WOULD happen — creates,
// updates with their before→after, corrections, and everything that will be skipped — before a
// single row is touched.
import React, { useState } from 'react';
import api from '../../services/api';
import { HRC, Card, Btn } from './hrkit';

const th = { fontSize: 10, color: HRC.navy, fontWeight: 800, textTransform: 'uppercase',
  letterSpacing: 0.4, padding: '7px 10px', textAlign: 'left', background: HRC.violet,
  borderBottom: `1px solid ${HRC.border}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 };
const td = { fontSize: 12, color: HRC.text, padding: '6px 10px', borderBottom: '1px solid #F1F0EE' };

const STATUS_COLOR = { present: HRC.green, absent: HRC.red, week_off: HRC.muted,
                       half_day: HRC.amber, leave: HRC.navy, holiday: '#8B5CF6' };
const Chip = ({ s }) => (
  <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
    color: STATUS_COLOR[s] || HRC.text, background: (STATUS_COLOR[s] || HRC.text) + '1E' }}>{s}</span>
);

const Stat = ({ label, value, tone, hint }) => (
  <div style={{ minWidth: 130 }}>
    <div style={{ fontSize: 10, color: HRC.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: tone || HRC.navy, marginTop: 2 }}>{value}</div>
    {hint && <div style={{ fontSize: 10.5, color: HRC.muted, marginTop: 1 }}>{hint}</div>}
  </div>
);

// A collapsible list — these can run to hundreds of rows and must not bury the summary.
function Section({ title, subtitle, count, tone, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;
  return (
    <div style={{ marginTop: 14, border: `1px solid ${HRC.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <button onClick={() => setOpen(!open)} style={{ width: '100%', textAlign: 'left', cursor: 'pointer',
        background: '#FAFAFC', border: 'none', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: tone || HRC.navy }}>{open ? '▾' : '▸'} {title}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: tone || HRC.navy,
          background: (tone || HRC.navy) + '1A', padding: '2px 7px', borderRadius: 10 }}>{count}</span>
        {subtitle && <span style={{ fontSize: 11, color: HRC.muted }}>{subtitle}</span>}
      </button>
      {open && <div style={{ maxHeight: 340, overflow: 'auto' }}>{children}</div>}
    </div>
  );
}

export default function ReportImport({ onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [allowBridge, setAllowBridge] = useState(false);

  const reset = () => { setPreview(null); setDone(null); setErr(''); setAllowBridge(false); };

  const doPreview = () => {
    if (!file) return;
    setBusy(true); reset();
    const fd = new FormData();
    fd.append('report', file);
    api.post('/hr/attendance/report-preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => setPreview(r.data?.data || null))
      .catch((e) => setErr(e.response?.data?.error || 'Could not read that file'))
      .finally(() => setBusy(false));
  };

  const doCommit = () => {
    if (!preview) return;
    setCommitting(true); setErr('');
    api.post('/hr/attendance/report-commit', {
      token: preview.token, original_name: preview.original_name, allow_bridge_overwrite: allowBridge,
    })
      .then((r) => { setDone(r.data?.data || {}); setPreview(null); if (onImported) onImported(); })
      .catch((e) => setErr(e.response?.data?.error || 'Import failed'))
      .finally(() => setCommitting(false));
  };

  const s = preview?.summary;

  return (
    <Card style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: HRC.navy }}>Import from the OnTime monthly report</div>
      <div style={{ fontSize: 11.5, color: HRC.muted, marginTop: 4, lineHeight: 1.6, maxWidth: 860 }}>
        Export the monthly attendance report from OnTime and upload it here. This recovers dates the
        bridge sent as zeros, and it is the only source that tells a <strong>weekly off</strong> apart
        from an <strong>absence</strong> — the bridge cannot, which is why 18.5% of every absence on
        record falls on a Sunday. Nothing is written until you review the preview and confirm.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 14 }}>
        <input type="file" accept=".xls,.xlsx" onChange={(e) => { setFile(e.target.files[0] || null); reset(); }}
          style={{ fontSize: 12 }} />
        <Btn variant="primary" onClick={doPreview} disabled={!file || busy}>
          {busy ? 'Reading…' : 'Preview import'}
        </Btn>
      </div>

      {err && (
        <div style={{ marginTop: 12, background: '#FDECEC', border: '1px solid #F3C0C0', color: '#A32020',
          borderRadius: 8, padding: '10px 14px', fontSize: 12.5 }}>{err}</div>
      )}

      {done && (
        <div style={{ marginTop: 12, background: '#E9F7EF', border: '1px solid #B7E1C6', color: '#1B7F4B',
          borderRadius: 8, padding: '10px 14px', fontSize: 12.5, lineHeight: 1.6 }}>
          <strong>Imported.</strong> {done.created} row(s) created, {done.updated} updated,{' '}
          {done.unchanged} already correct, {done.skipped_bridge} skipped (bridge already had a complete
          record), {done.queued_unmatched} day(s) of {done.queued_codes} unmatched device code(s) queued in Feed
          Health for a person to place,{' '}
          {done.skipped_leave} skipped (approved leave). Range {done.from_date} → {done.to_date}.
        </div>
      )}

      {preview && (
        <div style={{ marginTop: 16 }}>
          <div style={{ background: '#FAFAFC', border: `1px solid ${HRC.border}`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: HRC.navy, marginBottom: 10 }}>
              Preview — {preview.original_name} · <strong>{preview.month_label}</strong> ·{' '}
              {preview.from_date} → {preview.to_date} · nothing has been written yet
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <Stat label="Create" value={s.create} tone="#1B7F4B" hint="new rows" />
              <Stat label="Update" value={s.update} tone="#B4690E" hint="existing rows change" />
              <Stat label="Unchanged" value={s.unchanged} hint="already correct" />
              <Stat label="Corrections" value={s.status_fixes} tone={s.status_fixes ? '#B4690E' : undefined}
                hint={`${s.absent_to_week_off} absent→week off, ${s.absent_to_present} absent→present`} />
              <Stat label="Unmatched codes" value={s.unmatched_codes} tone={s.unmatched_codes ? '#A32020' : undefined}
                hint="cannot be imported" />
              <Stat label="Protected" value={s.bridge_newer} tone={s.bridge_newer ? '#A32020' : undefined}
                hint="bridge data is complete" />
            </div>
            {/* The file's own company banner is shown but explicitly disclaimed — it is not what
                decides where a row lands. */}
            <div style={{ marginTop: 12, fontSize: 11, color: HRC.muted, lineHeight: 1.6 }}>
              File header says <strong>{(preview.banner_companies || []).join(', ') || '—'}</strong>, which is
              ignored: every row is filed by the company on its ERP employee record. Parsed{' '}
              {preview.file_counts.cells} cell(s) for {preview.file_counts.employees} employee(s) —{' '}
              {preview.file_counts.punch} punch, {preview.file_counts.absent} absent,{' '}
              {preview.file_counts.week_off} weekly off. Skipped {preview.parse_skipped.na} “NA” cell(s)
              {preview.parse_skipped.out_of_month ? `, ${preview.parse_skipped.out_of_month} outside the month` : ''}.
            </div>
          </div>

          <Section title="Corrections — status will change" count={s.status_fixes} tone="#B4690E" defaultOpen
            subtitle="the reason for importing: absences the bridge invented">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Code</th><th style={th}>Name</th>
                <th style={th}>Company</th><th style={th}>From</th><th style={th}>To</th></tr></thead>
              <tbody>
                {preview.status_fixes.map((f, i) => (
                  <tr key={i}>
                    <td style={{ ...td, fontWeight: 700 }}>{f.date}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{f.ontime_code}</td>
                    <td style={td}>{f.full_name}</td>
                    <td style={{ ...td, color: HRC.muted }}>{f.company}</td>
                    <td style={td}><Chip s={f.from_status} /></td>
                    <td style={td}><Chip s={f.to_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Rows that would be created" count={s.create} tone="#1B7F4B"
            subtitle={`${s.create_present} present · ${s.create_absent} absent · ${s.create_week_off} weekly off`}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Code</th><th style={th}>Name</th>
                <th style={th}>Company</th><th style={th}>Status</th><th style={th}>In</th><th style={th}>Out</th></tr></thead>
              <tbody>
                {preview.creates.map((c, i) => (
                  <tr key={i}>
                    <td style={{ ...td, fontWeight: 700 }}>{c.date}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{c.ontime_code}</td>
                    <td style={td}>{c.full_name}</td>
                    <td style={{ ...td, color: HRC.muted }}>{c.company}</td>
                    <td style={td}><Chip s={c.to_status} /></td>
                    <td style={td}>{c.to_check_in || '—'}</td>
                    <td style={td}>{c.to_check_out || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Rows that would be updated" count={s.update} tone="#B4690E"
            subtitle="field-by-field before → after">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Code</th><th style={th}>Name</th>
                <th style={th}>Changes</th><th style={th}>Current source</th></tr></thead>
              <tbody>
                {preview.updates.map((u, i) => (
                  <tr key={i} style={u.bridge_newer ? { background: '#FDECEC' } : undefined}>
                    <td style={{ ...td, fontWeight: 700 }}>{u.date}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{u.ontime_code}</td>
                    <td style={td}>{u.full_name}</td>
                    <td style={{ ...td, fontSize: 11.5 }}>
                      {u.changes.map((c, j) => (
                        <span key={j} style={{ marginRight: 10 }}>
                          <span style={{ color: HRC.muted }}>{c.field}:</span>{' '}
                          <span style={{ textDecoration: 'line-through', color: HRC.muted }}>{String(c.from ?? '—')}</span>
                          {' → '}<strong>{String(c.to ?? '—')}</strong>
                        </span>
                      ))}
                      {u.bridge_newer && <strong style={{ color: '#A32020' }}> · skipped unless allowed below</strong>}
                    </td>
                    <td style={{ ...td, color: HRC.muted }}>{u.current_source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Protected — the bridge already holds a complete record" count={s.bridge_newer} tone="#A32020"
            subtitle="skipped by default so a good row is never silently downgraded">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Code</th><th style={th}>Name</th>
                <th style={th}>Changes the report would make</th><th style={th}>Bridge wrote</th></tr></thead>
              <tbody>
                {preview.bridge_newer.map((u, i) => (
                  <tr key={i}>
                    <td style={{ ...td, fontWeight: 700 }}>{u.date}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{u.ontime_code}</td>
                    <td style={td}>{u.full_name}</td>
                    <td style={{ ...td, fontSize: 11.5 }}>
                      {u.changes.map((c) => `${c.field}: ${c.from ?? '—'} → ${c.to ?? '—'}`).join(' · ')}
                    </td>
                    <td style={{ ...td, color: HRC.muted, whiteSpace: 'nowrap' }}>
                      {u.current_updated_at ? new Date(u.current_updated_at).toLocaleString('en-IN') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Device codes with no ERP employee" count={s.unmatched_codes} tone="#A32020"
            subtitle={`not written to anyone's attendance: their ${s.queue_days} day(s) go to the unmatched queue in Feed Health, where a person claims the code`}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Device code</th><th style={th}>Name in the report</th><th style={th}>Days</th><th style={th}>Note</th></tr></thead>
              <tbody>
                {preview.unmatched.map((u) => (
                  <tr key={u.ontime_code}>
                    <td style={{ ...td, fontWeight: 700, fontFamily: 'ui-monospace,Menlo,monospace' }}>{u.ontime_code}</td>
                    <td style={td}>{u.name || '—'}</td>
                    <td style={td}>{u.days}</td>
                    <td style={td}>{u.ambiguous ? 'two or more active employees share this code: queued, not guessed' : u.inactive_employee ? 'belongs to an inactive record' : 'no employee holds this code'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Employees absent from the file" count={(preview.missing_from_file || []).length} tone={HRC.muted}
            subtitle="active in these companies but nowhere in the report — no row will be written for them">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Code</th><th style={th}>Name</th><th style={th}>OnTime ID</th><th style={th}>Company</th></tr></thead>
              <tbody>
                {(preview.missing_from_file || []).map((m) => (
                  <tr key={m.employee_code}>
                    <td style={{ ...td, fontWeight: 700 }}>{m.employee_code}</td>
                    <td style={td}>{m.full_name}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{m.ontime_emp_id || '—'}</td>
                    <td style={{ ...td, color: HRC.muted }}>{m.company}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Approved leave — will be skipped" count={s.leave_blocked} tone={HRC.navy}
            subtitle="an approved leave day is never overwritten by any feed">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Date</th><th style={th}>Code</th><th style={th}>Name</th><th style={th}>Current</th></tr></thead>
              <tbody>
                {preview.leave_blocked.map((l, i) => (
                  <tr key={i}>
                    <td style={{ ...td, fontWeight: 700 }}>{l.date}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{l.ontime_code}</td>
                    <td style={td}>{l.full_name}</td>
                    <td style={td}><Chip s={l.from_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Cells the parser did not recognise" count={(preview.unknown_cells || []).length} tone="#A32020"
            subtitle="not imported — report these so the parser can be taught them">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Code</th><th style={th}>Day</th><th style={th}>Cell contents</th></tr></thead>
              <tbody>
                {(preview.unknown_cells || []).map((u, i) => (
                  <tr key={i}>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{u.ontime_code}</td>
                    <td style={td}>{u.day}</td>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>{JSON.stringify(u.raw)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${HRC.border}`,
            display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
            {s.bridge_newer > 0 && (
              <label style={{ fontSize: 12, color: '#A32020', display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                <input type="checkbox" checked={allowBridge} onChange={(e) => setAllowBridge(e.target.checked)} />
                Also overwrite the {s.bridge_newer} row(s) where the bridge holds a complete record
              </label>
            )}
            <div style={{ flex: 1 }} />
            <Btn variant="ghost" onClick={reset} disabled={committing}>Cancel</Btn>
            <Btn variant="primary" onClick={doCommit} disabled={committing || (!s.create && !s.update)}>
              {committing ? 'Importing…' : `Import ${s.create + (allowBridge ? s.update : s.update - s.bridge_newer)} row(s)`}
            </Btn>
          </div>
        </div>
      )}
    </Card>
  );
}
